import { Link } from 'react-router-dom';

interface CampaignSummary {
  id: string;
  name: string;
  status: string;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  progress: number;
}

interface RecentCampaignsProps {
  campaigns: CampaignSummary[];
}

const statusLabels: Record<string, string> = {
  active: 'Ativa',
  scheduled: 'Agendada',
  completed: 'Concluída',
  draft: 'Rascunho',
  paused: 'Pausada',
  cancelled: 'Cancelada',
};

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  scheduled: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400',
  completed: 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400',
  draft: 'bg-background-100 dark:bg-background-200 text-foreground-500',
  paused: 'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400',
  cancelled: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400',
};

export default function RecentCampaigns({ campaigns }: RecentCampaignsProps) {
  const hasData = campaigns.length > 0;

  return (
    <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-background-100 dark:border-background-200">
        <h3 className="text-sm font-heading font-semibold text-foreground-900 dark:text-foreground-100">Campanhas Recentes</h3>
        <Link
          to="/campaigns"
          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
        >
          Ver todas
          <i className="ri-arrow-right-line ml-1" />
        </Link>
      </div>

      {!hasData ? (
        <div className="py-16 flex flex-col items-center justify-center text-foreground-400">
          <i className="ri-megaphone-line text-4xl mb-3" />
          <p className="text-sm font-medium">Nenhuma campanha ainda</p>
          <p className="text-xs mt-1 mb-4">Crie sua primeira campanha para começar a enviar mensagens.</p>
          <Link
            to="/campaigns"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-xl transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            Criar Campanha
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 dark:border-background-200">
                <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Campanha</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Enviadas</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Entregues</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Lidas</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Respondidas</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider w-32">Progresso</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="border-b border-background-50 dark:border-background-200 hover:bg-background-50/50 dark:hover:bg-background-200/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="text-foreground-900 dark:text-foreground-100 font-medium">
                      {campaign.name}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[campaign.status] || ''}`}>
                      {statusLabels[campaign.status] || campaign.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right text-foreground-700 dark:text-foreground-300 tabular-nums">{campaign.sent.toLocaleString('pt-BR')}</td>
                  <td className="px-5 py-3.5 text-right text-foreground-700 dark:text-foreground-300 tabular-nums">{campaign.delivered.toLocaleString('pt-BR')}</td>
                  <td className="px-5 py-3.5 text-right text-foreground-700 dark:text-foreground-300 tabular-nums">{campaign.read.toLocaleString('pt-BR')}</td>
                  <td className="px-5 py-3.5 text-right text-foreground-700 dark:text-foreground-300 tabular-nums">{campaign.replied.toLocaleString('pt-BR')}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-background-200 dark:bg-background-300 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all duration-500"
                          style={{ width: `${campaign.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-foreground-500 w-8 tabular-nums">{campaign.progress}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}